import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetProductGroupsQuery,
  useDeleteProductGroupMutation,
} from "../query/productGroupsQuery";
import { useGetUnitsQuery } from "../query/unitsQuery";
import type { RestProductGroup } from "@KassaroAB/shared/types/productGroup";
import type { ProductGroupId } from "@KassaroAB/shared/types/ids";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";

const typeLabelKey: Record<ProductType, string> = {
  [ProductType.PRODUCT]: "productForm.typePRODUCT",
  [ProductType.SERVICE]: "productForm.typeSERVICE",
  [ProductType.GIFT_CARD]: "productForm.typeGIFT_CARD",
  [ProductType.TEXT]: "productForm.typeTEXT",
  [ProductType.SERVICE_ORDER_DEPOSIT]: "productForm.typeSERVICE_ORDER_DEPOSIT",
};

export const ProductGroupsView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("productGroups");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<ProductGroupId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: groups,
    isLoading,
    error: loadError,
  } = useGetProductGroupsQuery(scope);
  const { data: units } = useGetUnitsQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteProductGroupMutation(scope);

  const groupList = groups ?? [];
  const deleteName = groupList.find((group) => group.id === deleteId)?.name ?? "";
  const unitsById = new Map((units ?? []).map((unit) => [unit.id, unit.name]));

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("productGroupsView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && groupList.length === 0) {
    return (
      <PageContent title={t("productGroupsView.title")}>
        <LoadingContainer message={t("productGroupsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("productGroupsView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("productGroupsView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">{t("productGroupsView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("productGroupsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {groupList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("productGroupsView.tableName")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("productGroupsView.tableVat")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("productGroupsView.tableType")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("productGroupsView.tableUnit")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {groupList.map((group: RestProductGroup) => (
                <TableRow key={group.id} to={`${pagePath}/${group.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={group.name}
                      origin={group.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>
                    {group.VAT !== null && group.VAT !== undefined
                      ? `${group.VAT}%`
                      : "—"}
                  </TableCell>
                  <TableCell noWrap>
                    {group.type ? t(typeLabelKey[group.type]) : "—"}
                  </TableCell>
                  <TableCell noWrap>
                    {group.unitId
                      ? (unitsById.get(group.unitId) ?? "—")
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(group.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("productGroupsView.empty")} ${t("productGroupsView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("productGroupForm.deleteGroup")}
        message={t("productGroupsView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
